import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Tag as TagIcon, ZoomIn, Save } from "lucide-react";
import { API } from "../lib/api";
import { getToken } from "../lib/auth";

/**
 * Photographer-only lightbox for tagging photos.
 * Loads the un-watermarked source-thumb (much sharper / easier to read bib numbers),
 * supports click-to-zoom, ←/→ keyboard nav, and inline tag editing.
 *
 * Props:
 *   - photos: array of {id, event_name, manual_numbers, detected_numbers, tags_overridden}
 *   - index: current index in photos[]
 *   - onClose: close handler
 *   - onIndexChange: (newIndex) => void
 *   - onSaveTags: async (photoId, numbers: string[]) => void
 */
export default function PhotoTagLightbox({ photos, index, onClose, onIndexChange, onSaveTags }) {
  const [zoomed, setZoomed] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);

  const photo = photos[index];

  // When index changes, sync local input with that photo's current tags
  useEffect(() => {
    if (!photo) return;
    const cur = photo.tags_overridden
      ? (photo.manual_numbers || [])
      : [...new Set([...(photo.detected_numbers || []), ...(photo.manual_numbers || [])])];
    setTagInput(cur.join(", "));
    setZoomed(false);
  }, [photo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const goPrev = useCallback(() => {
    onIndexChange((index - 1 + photos.length) % photos.length);
  }, [index, photos.length, onIndexChange]);

  const goNext = useCallback(() => {
    onIndexChange((index + 1) % photos.length);
  }, [index, photos.length, onIndexChange]);

  const handleSave = useCallback(async () => {
    if (!photo) return;
    setSaving(true);
    try {
      const nums = tagInput.split(/[,\s]+/).filter(Boolean);
      await onSaveTags(photo.id, nums);
    } finally {
      setSaving(false);
    }
  }, [photo, tagInput, onSaveTags]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT") return; // don't hijack typing
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [goPrev, goNext, onClose]);

  if (!photo) return null;

  // Un-watermarked source thumb — token-gated
  const sourceUrl = `${API}/photos/${photo.id}/source-thumb?t=${encodeURIComponent(getToken() || "")}`;

  const aiNumbers = photo.detected_numbers || [];
  const manualNumbers = photo.manual_numbers || [];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex flex-col"
      data-testid="tag-lightbox"
      onClick={onClose}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between p-4 text-white text-sm border-b border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-white/50 tabular-nums whitespace-nowrap">
            {index + 1} / {photos.length}
          </span>
          <span className="text-white/30">·</span>
          <span className="truncate">{photo.event_name}</span>
          {aiNumbers.length > 0 && (
            <>
              <span className="text-white/30">·</span>
              <span className="text-white/50 text-xs uppercase tracking-wider">AI hittade:</span>
              <div className="flex gap-1">
                {aiNumbers.slice(0, 5).map((n) => (
                  <span key={n} className="bg-blue-600/40 text-blue-200 text-xs font-bold uppercase px-1.5 py-0.5">
                    #{n}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/10 transition-colors flex-shrink-0"
          aria-label="Stäng"
          data-testid="tag-lightbox-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Main image area */}
      <div
        className="flex-1 relative flex items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {photos.length > 1 && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 hover:bg-brand text-white flex items-center justify-center z-10 transition-colors"
              aria-label="Föregående"
              data-testid="tag-lightbox-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 hover:bg-brand text-white flex items-center justify-center z-10 transition-colors"
              aria-label="Nästa"
              data-testid="tag-lightbox-next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        <div
          className="relative max-w-full max-h-full overflow-auto cursor-zoom-in"
          onClick={() => setZoomed((z) => !z)}
          data-testid="tag-lightbox-image-wrap"
        >
          <img
            src={sourceUrl}
            alt={photo.event_name || "Foto"}
            className={`block transition-transform duration-200 ${
              zoomed ? "scale-[2.5] cursor-zoom-out" : "max-w-[95vw] max-h-[72vh] cursor-zoom-in"
            }`}
            data-testid="tag-lightbox-image"
          />
        </div>

        {!zoomed && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white/70 text-xs uppercase tracking-wider px-3 py-1.5 flex items-center gap-2 pointer-events-none">
            <ZoomIn className="w-3.5 h-3.5" /> Klicka för att zooma · ←/→ bläddra · Esc stänger
          </div>
        )}
      </div>

      {/* Bottom bar — tag input */}
      <div
        className="border-t border-white/10 p-4 bg-black/60"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <TagIcon className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-wider text-white/70 font-bold">
              Startnummer
            </span>
            {manualNumbers.length > 0 && (
              <>
                <span className="text-white/30 text-xs">·</span>
                <span className="text-white/50 text-xs">
                  Manuellt: {manualNumbers.map((n) => `#${n}`).join(", ")}
                </span>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <input
              autoFocus
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
              }}
              placeholder="143, 27, 89  ·  tomt = inga taggar"
              className="input-field flex-1 text-base"
              data-testid="tag-lightbox-input"
            />
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary inline-flex items-center gap-2 px-5 disabled:opacity-50"
              data-testid="tag-lightbox-save"
            >
              <Save className="w-4 h-4" /> {saving ? "Sparar..." : "Spara"}
            </button>
          </div>
          <p className="text-[11px] text-white/40 mt-2">
            Komma- eller mellanslagsseparerade siffror. Spara tomt för att rensa alla taggar.
            Tryck <kbd className="px-1 bg-white/10">Enter</kbd> för spara · <kbd className="px-1 bg-white/10">→</kbd> nästa bild.
          </p>
        </div>
      </div>
    </div>
  );
}
